import http from 'utils/http';
import invariant from 'invariant';
import {
  ChangeProfilePasswordPayload,
  CreateUserPayload,
  DeleteUserPayload,
  User,
} from '../index';
import { LimitResponse } from '../types';

export function fetchUsers(params: any = {}) {
  return http.get<never, User[]>('api/user', { params });
}

export function readUser(user: Pick<User, 'id'>) {
  return http.get<never, User>(`api/user/${user.id}`);
}

export function createUser(payload: CreateUserPayload) {
  return http.post<never, User>('api/user', payload);
}

export function updateUser(user: User) {
  invariant(user.id, 'User id is required to perform update call');

  return http.put(`api/user/${user.id}`, user);
}

export function changeUserPassword(user: Pick<User, 'id'>, password: string) {
  invariant(user.id, 'User id is required to perform change password call');

  return http.post(`api/user/${user.id}/password`, {
    password,
  });
}

export function patchUpdateUser(user: Partial<User> & Pick<User, 'id'>) {
  invariant(user.id, 'User id is required to perform update call');

  return http.patch(`api/user/${user.id}`, user);
}

export function deleteUser({ id }: DeleteUserPayload) {
  invariant(id, 'User id is required to perform delete call');

  return http.delete(`api/user/${id}`);
}

export function updateProfile({
  firstName,
  lastName,
  email,
  language,
}: Partial<User>) {
  return http.put('api/profile/update', {
    firstName,
    lastName,
    email,
    language,
  });
}

export function changeProfilePassword({
  currentPassword,
  password,
}: ChangeProfilePasswordPayload) {
  return http.post('api/profile/change-password', {
    currentPassword,
    password,
  });
}

export function fetchUserApiCredentials(user: Pick<User, 'id'>) {
  return http.get<never, { client_id: string; secret: string }[]>(
    `api/user/${user.id}/client-credentials`,
  );
}

export function createUserApiCredentials(user: Pick<User, 'id'>) {
  return http.post(`api/user/${user.id}/client-credentials`);
}

export function removeUserApiCredentials(
  user: Pick<User, 'id'>,
  clientId: string,
) {
  return http.delete(`api/user/${user.id}/client-credentials/${clientId}`);
}

export function regeneratePasswordLink(user: Pick<User, 'id'>) {
  return http.put(`api/user/${user.id}/password-link`);
}

export function readUserSubAccount(userId: User['id']) {
  return http.get<never, LimitResponse>(`api/user/sub-account/${userId}`);
}

export function updateUserSubAccount(userId: User['id'], data: any) {
  return http.put<never, LimitResponse>(`api/user/sub-account/${userId}`, {
    enabled: data.enabled,
    maxAssets: +data.maxAssets,
    maxAssetTypes: +data.maxAssetTypes,
    maxClusters: +data.maxClusters,
    maxStorage: +data.maxStorage,
  });
}
